import React, {useEffect, useState} from 'react';
import {Modal, Text, Button, Icon} from '@/ui-kits';
import {FileSerializableItem} from '@/interface';
import {API, imageFileRegex, pdfFileRegex} from '@/constants';
import {downloadFileFromUrl, fetchAndDownloadFile} from '@/utils/common_functions';
import {getDocumentBytes, getSecondOpinionReport} from '@/services/api';

interface FileViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: Pick<FileSerializableItem, 'id' | 'name' | 'url'> | null;
  showDownloadButton?: boolean;
  isSecure?: boolean;
  is_second_opinion_report?: boolean;
  file_type?: 'pdf' | 'image' | 'other';
}

export const FileViewerModal: React.FC<FileViewerModalProps> = ({
  isOpen,
  onClose,
  file,
  showDownloadButton = false,
  isSecure = true,
  is_second_opinion_report = false,
  file_type = 'other',
}) => {
  const [imageZoom, setImageZoom] = useState(100);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch document binary from backend when modal opens
  useEffect(() => {
    let revokedUrl: string | null = null;

    const loadDocument = async () => {
      if (!isOpen || !file?.id) {
        setBlobUrl(null);
        setLoadError(null);
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        let blob: Blob;
        if (is_second_opinion_report) {
          blob = await getSecondOpinionReport(file.id);
        } else {
          blob = await getDocumentBytes(file.id);
        }
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        revokedUrl = url;
      } catch (err) {
        console.error('Failed to load document blob', err);
        setLoadError('Unable to preview this document. Please try downloading it.');
        setBlobUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [isOpen, file?.id]);

  if (!isOpen || !file) return null;

  const fallbackUrl = file.url || '';
  const url = isSecure ? blobUrl : fallbackUrl;
  const name = file.name?.toLowerCase() || '';
  const isImage = file_type === 'image' || imageFileRegex.test(name) || imageFileRegex.test(url || '');
  const isPdf = file_type === 'pdf' || pdfFileRegex.test(name) || pdfFileRegex.test(url || '');

  const handleZoomIn = () => setImageZoom(z => Math.min(200, z + 25));
  const handleZoomOut = () => setImageZoom(z => Math.max(50, z - 25));
  const handleResetZoom = () => setImageZoom(100);

  function handleDownload(url: string, filename: string) {
    const blobUrl = `blob://${API.currentEnv ? API.webAppUrls[API.currentEnv as keyof typeof API.webAppUrls] : API.webAppUrls.dev}${url}`;
    const isBlobUrl = url.startsWith(blobUrl);
    if (isBlobUrl) {
      downloadFileFromUrl(url, filename);
    } else {
      fetchAndDownloadFile(url, filename);
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} className="rounded-xl w-[96vw] sm:w-[90vw] lg:w-[80vw] xl:w-[70vw] h-[90vh] px-2 sm:px-4 bg-white">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-2 gap-2">
          <Text variant="title" className="text-ui_blue truncate">
            {file.name}
          </Text>
          <div className="flex items-center gap-4">
            {showDownloadButton && url && (
              <button
                onClick={() => handleDownload(url, file.name)}
                className="text-ui_blue hover:text-ui_blue_light transition-colors p-1.5"
                title="Download report">
                <Icon type="download_btn_nobg" className="size-7" />
              </button>
            )}
            <button onClick={onClose} className="text-ui_blue hover:text-ui_blue_light transition-colors p-1.5" title="Close">
              <Icon type="close" className="size-7" />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden relative">
          {isLoading && (
            <div className="w-full h-full flex items-center justify-center">
              <Text className="text-ui_blue text-sm">Loading document...</Text>
            </div>
          )}
          {!isLoading && loadError && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-4 text-center">
              <Text className="text-ui_blue text-sm">{loadError}</Text>
              {fallbackUrl && (
                <a href={fallbackUrl} target="_blank" rel="noreferrer">
                  <Button title="Open in new tab" variant="primary" />
                </a>
              )}
            </div>
          )}
          {!isLoading && !loadError && url && (
            <>
              {isImage ? (
                <>
                  <div className="w-full h-full overflow-auto p-2">
                    <div
                      style={{
                        textAlign: 'center',
                        minHeight: '100%',
                      }}>
                      <img
                        src={url}
                        alt={file.name}
                        style={{
                          width: imageZoom <= 100 ? `${imageZoom}%` : `${imageZoom}%`,
                          maxWidth: imageZoom <= 100 ? '100%' : 'none',
                          height: 'auto',
                          display: 'inline-block',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-1 sm:gap-2 bg-white rounded-lg shadow-lg px-2 sm:px-3 py-2 border border-gray-200 z-10 pointer-events-auto">
                    <button onClick={handleZoomOut} className="text-ui_blue hover:bg-gray-100 p-1 sm:p-1.5 rounded transition-colors" title="Zoom out">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <Text className="text-ui_blue text-[10px] sm:text-xs font-FigtreeSemiBold min-w-[2.5rem] sm:min-w-[3rem] text-center">{imageZoom}%</Text>
                    <button onClick={handleZoomIn} className="text-ui_blue hover:bg-gray-100 p-1 sm:p-1.5 rounded transition-colors" title="Zoom in">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <div className="w-px h-4 sm:h-5 bg-gray-300 mx-0.5 sm:mx-1"></div>
                    <button
                      onClick={handleResetZoom}
                      className="text-ui_blue hover:bg-gray-100 px-1.5 sm:px-2 py-1 rounded text-[10px] sm:text-xs transition-colors font-FigtreeSemiBold"
                      title="Reset zoom">
                      Reset
                    </button>
                  </div>
                </>
              ) : isPdf ? (
                <iframe title={file.name} src={`${url}#toolbar=0&navpanes=0&scrollbar=0`} className="w-full h-full border-0" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                  <Text className="text-ui_blue text-sm sm:text-base text-center">Preview not available. You can download and view the file.</Text>
                  <a href={url} target="_blank" rel="noreferrer">
                    <Button title="Open in new tab" variant="primary" />
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
